import AddIcon from '@mui/icons-material/Add';
import ForumIcon from '@mui/icons-material/Forum';
import SendIcon from '@mui/icons-material/Send';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useMijnContactmomenten } from '../api/contactmomenten.ts';
import { useCvWachtrij } from '../api/cvs.ts';
import { useKandidaten } from '../api/kandidaten.ts';
import type { LopendeFase } from '../api/typen.ts';
import { useVacatures } from '../api/vacatures.ts';
import { useLopendeVoordrachten } from '../api/voordrachten.ts';
import { useSessie } from '../auth/SessieContext.tsx';
import { ContactmomentDialoog } from '../componenten/ContactmomentDialoog.tsx';
import { ContactmomentenLijst } from '../componenten/ContactmomentenLijst.tsx';
import { Laadstatus } from '../componenten/Laadstatus.tsx';
import { Pagina } from '../componenten/Pagina.tsx';
import { StatusChip } from '../componenten/StatusChip.tsx';
import { VoordrachtDialoog } from '../componenten/VoordrachtDialoog.tsx';
import { datum, volledigeNaam } from '../helpers/formatteer.ts';
import { lopendeFaseLabels } from '../helpers/labels.ts';
import { KandidaatFormulier } from './kandidaten/KandidaatFormulier.tsx';
import { VacatureFormulier } from './vacatures/VacatureFormulier.tsx';

const fases: LopendeFase[] = ['voorbereiding', 'voorgesteld', 'gesprek', 'aanbod'];

function Kaart({ titel, naar, children }: { titel: string; naar: string; children: ReactNode }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={titel}
        slotProps={{ title: { variant: 'h6' } }}
        action={
          <Button component={RouterLink} to={naar} nativeButton={false} size="small">
            Alles
          </Button>
        }
        sx={{ pb: 0 }}
      />
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Voordrachten() {
  const voordrachten = useLopendeVoordrachten(undefined);
  return (
    <Kaart titel="Lopende voordrachten" naar="/voordrachten">
      <Laadstatus query={voordrachten}>
        {(lijst) => (
          <Stack spacing={1}>
            <Typography variant="h4" component="div">
              {lijst.length}
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              {fases.map((fase) => (
                <Chip
                  key={fase}
                  label={`${lopendeFaseLabels[fase]}: ${lijst.filter((v) => v.fase === fase).length}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Laadstatus>
    </Kaart>
  );
}

function NieuweKandidaten() {
  const kandidaten = useKandidaten({ status: ['nieuw'], limiet: 6 });
  return (
    <Kaart titel="Nieuwe kandidaten" naar="/kandidaten?status=nieuw">
      <Laadstatus query={kandidaten} leeg="Geen nieuwe kandidaten.">
        {(lijst) => (
          <List dense disablePadding>
            {lijst.map((kandidaat) => (
              <ListItem key={kandidaat.id} disableGutters>
                <ListItemText
                  primary={
                    <Link component={RouterLink} to={`/kandidaten/${kandidaat.id}`} underline="hover">
                      {volledigeNaam(kandidaat)}
                    </Link>
                  }
                  secondary={`Geregistreerd ${datum(kandidaat.aangemaaktOp)}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Laadstatus>
    </Kaart>
  );
}

function OpenVacatures() {
  const vacatures = useVacatures({ status: 'open', limiet: 6 });
  return (
    <Kaart titel="Open vacatures" naar="/vacatures?status=open">
      <Laadstatus query={vacatures} leeg="Geen open vacatures.">
        {(lijst) => (
          <List dense disablePadding>
            {lijst.map((vacature) => (
              <ListItem
                key={vacature.id}
                disableGutters
                secondaryAction={<StatusChip soort="vacature" waarde={vacature.status} />}
              >
                <ListItemText
                  primary={
                    <Link component={RouterLink} to={`/vacatures/${vacature.id}`} underline="hover">
                      {vacature.titel}
                    </Link>
                  }
                  secondary={`Gewijzigd ${datum(vacature.gewijzigdOp)}`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Laadstatus>
    </Kaart>
  );
}

function CvWachtrij() {
  const wachtrij = useCvWachtrij({ limiet: 100 });
  return (
    <Kaart titel="Cv-wachtrij" naar="/cvs">
      <Laadstatus query={wachtrij}>
        {(lijst) => (
          <>
            <Typography variant="h4" component="div">
              {lijst.length >= 100 ? '100+' : lijst.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              cv's wachten op verwerking
            </Typography>
          </>
        )}
      </Laadstatus>
    </Kaart>
  );
}

function MijnGesprekken() {
  const gesprekken = useMijnContactmomenten({ limiet: 5 });
  return (
    <Kaart titel="Mijn laatste gesprekken" naar="/gesprekken">
      <ContactmomentenLijst
        query={gesprekken}
        compact
        toon={{ gebruiker: false }}
        leeg="Je hebt nog geen gesprekken vastgelegd."
      />
    </Kaart>
  );
}

/**
 * Het dashboard: de stand van de pijplijn, nieuwe kandidaten, open vacatures, de cv-wachtrij en je laatste
 * gesprekken, met knoppen voor de meest voorkomende acties.
 * @returns De pagina.
 */
export function Dashboard() {
  const { ik } = useSessie();
  const navigeer = useNavigate();
  const [kandidaat, setKandidaat] = useState(false);
  const [vacature, setVacature] = useState(false);
  const [gesprek, setGesprek] = useState(false);
  const [voordragen, setVoordragen] = useState(false);

  return (
    <Pagina
      titel={`Welkom, ${ik?.gebruiker.naam.split(' ')[0] ?? ''}`}
      ondertitel="Dit is de stand van zaken vandaag."
      acties={
        <>
          <Button variant="outlined" startIcon={<ForumIcon />} onClick={() => setGesprek(true)}>
            Gesprek vastleggen
          </Button>
          <Button variant="outlined" startIcon={<SendIcon />} onClick={() => setVoordragen(true)}>
            Voordragen
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setVacature(true)}>
            Nieuwe vacature
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setKandidaat(true)}>
            Nieuwe kandidaat
          </Button>
        </>
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <Voordrachten />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <CvWachtrij />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <OpenVacatures />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <NieuweKandidaten />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <MijnGesprekken />
        </Grid>
      </Grid>
      <KandidaatFormulier
        open={kandidaat}
        onSluit={() => setKandidaat(false)}
        onOpgeslagen={(nieuw) => void navigeer(`/kandidaten/${nieuw.id}`)}
      />
      <VacatureFormulier
        open={vacature}
        onSluit={() => setVacature(false)}
        onOpgeslagen={(nieuw) => void navigeer(`/vacatures/${nieuw.id}`)}
      />
      <ContactmomentDialoog open={gesprek} onSluit={() => setGesprek(false)} />
      <VoordrachtDialoog
        open={voordragen}
        onSluit={() => setVoordragen(false)}
        onGemaakt={(nieuw) => void navigeer(`/voordrachten/${nieuw.id}`)}
      />
    </Pagina>
  );
}
